// Pure helpers for archive / delete flows (no DOM, unit-testable).
import type { ArchiveResult, Manifest, RemoteArchiveResult } from "../api/types";

const CONNECTOR_NAMES: Record<string, string> = {
  "nextcloud-deck": "Nextcloud Deck",
  "msgraph-todo": "Microsoft To Do",
  trello: "Trello",
  jira: "Jira",
};

/** Full, human name of a connector ("Nextcloud Deck"), preferring the manifest's display name. */
export function connectorName(type: string, manifests?: Manifest[] | null): string {
  const m = manifests?.find((x) => x.connector_type === type);
  const fromManifest = m?.display_name ?? m?.name;
  if (typeof fromManifest === "string" && fromManifest.trim()) return fromManifest;
  return CONNECTOR_NAMES[type] ?? type;
}

/**
 * Whether a manifest declares a capability. Capabilities may be an object
 * (`{ archive_collections: true }`) or a list of names (`["archive_collections"]`).
 * Unknown → `null` (manifest missing / not declared at all).
 */
export function manifestCapability(manifest: Manifest | undefined, name: string): boolean | null {
  if (!manifest) return null;
  const caps = manifest.capabilities;
  if (Array.isArray(caps)) return caps.includes(name);
  if (caps && typeof caps === "object") {
    if (!(name in caps)) return false;
    return !!(caps as Record<string, unknown>)[name];
  }
  return null;
}

export function supportsArchive(type: string, manifests?: Manifest[] | null): boolean | null {
  return manifestCapability(manifests?.find((m) => m.connector_type === type), "archive_collections");
}

export type ArchiveOp = "archive" | "unarchive";

/** One human sentence per remote binding outcome. */
export function remoteOutcomeText(r: RemoteArchiveResult, op: ArchiveOp, manifests?: Manifest[] | null): string {
  const tool = connectorName(r.connector_type, manifests);
  const local = op === "archive" ? "archived in ToDoItAll only" : "unarchived in ToDoItAll only";
  switch (r.result) {
    case "archived":
      return `Archived in ${tool}`;
    case "unarchived":
      return `Restored in ${tool}`;
    case "unsupported":
      return `${tool} doesn't support archiving — ${local}`;
    case "skipped":
      return `${tool} left unchanged — ${local}`;
    case "error":
      return `Couldn't ${op} in ${tool}${r.error ? ` (${r.error})` : ""} — ${local}`;
    default:
      return `${tool}: ${String(r.result)}`;
  }
}

export interface ArchiveSummary {
  kind: "success" | "error";
  message: string;
  /** Distinct per-binding sentences (also part of `message`). */
  lines: string[];
}

/**
 * Summarise one or more ArchiveResults (a collection, or all collections of a space) for a toast.
 * `subject` is e.g. `"Ops"` or `space "General"`.
 */
export function summariseArchive(
  results: ArchiveResult[],
  op: ArchiveOp,
  subject: string,
  manifests?: Manifest[] | null,
): ArchiveSummary {
  const lines: string[] = [];
  for (const res of results) {
    for (const r of res.remote ?? []) {
      const t = remoteOutcomeText(r, op, manifests);
      if (!lines.includes(t)) lines.push(t);
    }
  }
  const verb = op === "archive" ? "Archived" : "Unarchived";
  const head = `${verb} ${subject}`;
  const hasError = results.some((res) => (res.remote ?? []).some((r) => r.result === "error"));
  const message = lines.length ? `${head}. ${lines.join(" · ")}` : `${head}.`;
  return { kind: hasError ? "error" : "success", message, lines };
}
